/**
 * @fileoverview Pure GeoJSON polygon → normalised SVG path helper (WEB-648).
 *
 * Converts a neighborhood boundary + bbox (API-1657 `?include=boundary`) into
 * an SVG `d` string fitted to a unit viewBox `[0 0 1 1]`. Consumers scale the
 * same path to any size (44px hoodmark, hero graticule) without re-projecting.
 *
 * Coordinate conventions:
 * - Input is GeoJSON `[lon, lat]` with north = larger lat.
 * - SVG Y grows downward, so lat is flipped: north renders at the top.
 * - Non-square bboxes are letterboxed into the unit square (no distortion).
 */

import type { GeoJsonBbox, GeoJsonPolygon } from '@loci/validation';

/** Precision for emitted path coordinates (unit viewBox — 4 decimals is plenty). */
const PATH_COORD_PRECISION = 4;

/** Projects `[lon, lat]` into a unit viewBox fitted to one bbox. */
export interface UnitBboxProjector {
  /** @returns `[x, y]` in the unit viewBox, or `null` for non-finite input. */
  project(lon: number, lat: number): [number, number] | null;
}

/**
 * Build the lon/lat → unit-viewBox projection for one bbox.
 *
 * Exported so every consumer of a given extent — footprint outlines AND the
 * WEB-654 hero graticule's coordinate lines — shares one implementation. Two
 * copies of this arithmetic would drift and the graticule would stop aligning
 * with the footprints it is supposed to register against.
 *
 * @returns A projector, or `null` when the bbox cannot define one (non-finite
 *   numbers, zero/negative area).
 */
export function createUnitBboxProjector(
  bbox: GeoJsonBbox | null | undefined,
): UnitBboxProjector | null {
  if (!bbox) return null;

  const [minLon, minLat, maxLon, maxLat] = bbox;
  if (
    !Number.isFinite(minLon) ||
    !Number.isFinite(minLat) ||
    !Number.isFinite(maxLon) ||
    !Number.isFinite(maxLat)
  ) {
    return null;
  }

  const width = maxLon - minLon;
  const height = maxLat - minLat;
  if (!(width > 0) || !(height > 0)) return null;

  // Fit the bbox into the unit square without distortion (letterbox / pillarbox).
  const scale = 1 / Math.max(width, height);
  const offsetX = (1 - width * scale) / 2;
  const offsetY = (1 - height * scale) / 2;

  return {
    project(lon: number, lat: number): [number, number] | null {
      if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
      const x = offsetX + (lon - minLon) * scale;
      // Flip Y so geographic north (larger lat) maps to SVG top (smaller y).
      const y = offsetY + (maxLat - lat) * scale;
      return [roundCoord(x), roundCoord(y)];
    },
  };
}

/**
 * Convert a GeoJSON Polygon + RFC 7946 bbox into a unit-viewBox SVG path `d`.
 *
 * Pass the polygon's own bbox to fill the unit square (44px hoodmark). Pass a
 * shared extent — e.g. the union of every neighborhood's bbox — to place
 * several footprints in one another's true relative positions (WEB-654 hero).
 *
 * @returns Normalised path string, or `null` when the geometry / bbox cannot
 *   produce a drawable outline (empty ring, non-finite numbers, zero-area bbox).
 */
export function boundaryToFootprintPath(
  boundary: GeoJsonPolygon | null | undefined,
  bbox: GeoJsonBbox | null | undefined,
): string | null {
  if (!boundary || boundary.type !== 'Polygon') return null;

  const rings = boundary.coordinates;
  if (!Array.isArray(rings) || rings.length === 0) return null;

  const projector = createUnitBboxProjector(bbox);
  if (!projector) return null;

  const parts: string[] = [];

  for (const ring of rings) {
    if (!Array.isArray(ring) || ring.length < 4) return null;

    // Drop the closing duplicate vertex that GeoJSON rings conventionally carry;
    // SVG `Z` closes the subpath.
    const end =
      ring.length > 1 && isSameVertex(ring[0], ring[ring.length - 1])
        ? ring.length - 1
        : ring.length;
    if (end < 3) return null;

    let subpath = '';
    for (let i = 0; i < end; i += 1) {
      const vertex = ring[i];
      if (!Array.isArray(vertex) || vertex.length < 2) return null;
      const projected = projector.project(vertex[0], vertex[1]);
      if (!projected) return null;
      const [x, y] = projected;
      subpath += i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`;
    }
    subpath += ' Z';
    parts.push(subpath);
  }

  return parts.length > 0 ? parts.join(' ') : null;
}

function roundCoord(value: number): number {
  const factor = 10 ** PATH_COORD_PRECISION;
  return Math.round(value * factor) / factor;
}

function isSameVertex(a: readonly number[] | undefined, b: readonly number[] | undefined): boolean {
  if (!a || !b || a.length < 2 || b.length < 2) return false;
  return a[0] === b[0] && a[1] === b[1];
}
